import { useCallback, useEffect } from "react"
import { safeJsonParse } from "../../../helpers/safeJsonParse"

export const MOLLIE_PENDING_KEY_PREFIX = "mollie-pending-payment:"

export function getMolliePendingKey(bookingId: string): string {
    return `${MOLLIE_PENDING_KEY_PREFIX}${bookingId}`
}

export interface MolliePendingPaymentEntry {
    scheduledPaymentId: string | null
    molliePaymentId: string | null
    attemptKey: string | null
    createdAt: number
    redirectUrl: string | null
    // Planner-Revision-1 (Phase 9) - extended fields for D-12 Retry-Flow in 09-02
    // Old localStorage entries without these fields remain readable (optional types).
    bookingId?: string
    method?: string
    locale?: string // i18next locale BEFORE mapLocaleToMollie (de/de-DU/de-SIE/en)
}

const TTL_MS = 24 * 60 * 60 * 1000

function safeGetItem(key: string): string | null {
    try {
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

function safeRemoveItem(key: string): void {
    try {
        window.localStorage.removeItem(key)
    } catch {
        /* no-op */
    }
}

/**
 * Abgelaufen = älter als 24 h oder ohne gültiges `createdAt`. Leser prüfen das selbst, weil die
 * Rückkehrseite localStorage synchron im Render liest, also vor jedem Aufräumlauf.
 */
export function isExpiredMolliePendingEntry(entry: Pick<MolliePendingPaymentEntry, "createdAt">): boolean {
    return typeof entry.createdAt !== "number" || Date.now() - entry.createdAt > TTL_MS
}

/** Entfernt abgelaufene und kaputte Einträge. Läuft vor jedem neuen Eintrag (BookingSummary) und auf der Rückkehrseite. */
export function removeStaleMolliePendingEntries(): void {
    const toRemove: string[] = []
    try {
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i)
            if (!key || !key.startsWith(MOLLIE_PENDING_KEY_PREFIX)) continue
            const entry = safeJsonParse<MolliePendingPaymentEntry>(safeGetItem(key))
            if (!entry || isExpiredMolliePendingEntry(entry)) {
                toRemove.push(key)
            }
        }
    } catch {
        /* no-op */
    }
    toRemove.forEach(safeRemoveItem)
}

export function useMolliePendingPayment(bookingId: string | null) {
    useEffect(() => {
        removeStaleMolliePendingEntries()
    }, [])

    const get = useCallback((): MolliePendingPaymentEntry | null => {
        if (!bookingId) return null
        const key = getMolliePendingKey(bookingId)
        const raw = safeGetItem(key)
        const entry = safeJsonParse<MolliePendingPaymentEntry>(raw)
        if (raw && (!entry || isExpiredMolliePendingEntry(entry))) {
            // Kaputt oder abgelaufen: wegräumen, nicht als Retry-Kontext verwenden
            safeRemoveItem(key)
            return null
        }
        return entry
    }, [bookingId])

    const clear = useCallback((): void => {
        if (!bookingId) return
        safeRemoveItem(getMolliePendingKey(bookingId))
    }, [bookingId])

    return { get, clear }
}
